/**
 * Post status constants matching backend PostStatus enum
 */
export const POST_STATUS = {
  DRAFT: 'DRAFT',
  PUBLISHED: 'PUBLISHED',
  ARCHIVED: 'ARCHIVED'
};

/**
 * BlogPostData class for handling blog post objects
 * Mirrors the backend BlogPostResponse model
 */
export class BlogPostData {
  static STATUS = POST_STATUS;

  constructor(data) {
    this.post_id = data.post_id;
    this.title = data.title || '';
    this.slug = data.slug || '';
    this.excerpt = data.excerpt || null;
    this.content = data.content || '';
    this.featured_image = data.featured_image || null;
    this.status = data.status || POST_STATUS.DRAFT;
    this.is_pinned = data.is_pinned || false;
    this.author_id = data.author_id || null;
    this.author_name = data.author_name || null;
    this.published_at = data.published_at;
    this.created_at = data.created_at;
    this.updated_at = data.updated_at;
  }

  /**
   * Check if post is published
   */
  get isPublished() {
    return this.status === POST_STATUS.PUBLISHED;
  }

  /**
   * Check if post is a draft
   */
  get isDraft() {
    return this.status === POST_STATUS.DRAFT;
  }

  /**
   * Check if post is archived
   */
  get isArchived() {
    return this.status === POST_STATUS.ARCHIVED;
  }

  /**
   * Check if post is pinned
   */
  get isPinned() {
    return this.is_pinned === true;
  }

  /**
   * Get formatted publish date
   */
  get formattedPublishDate() {
    if (!this.published_at) return null;
    return new Date(this.published_at).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  /**
   * Create BlogPostData from API response
   */
  static fromApiResponse(data) {
    return new BlogPostData(data);
  }

  /**
   * Create array of BlogPostData from API response list
   */
  static fromApiResponseList(dataList = []) {
    return dataList.map(BlogPostData.fromApiResponse);
  }

  /**
   * Convert to format suitable for API create requests
   */
  toCreateFormat() {
    return {
      title: this.title,
      slug: this.slug,
      excerpt: this.excerpt,
      content: this.content,
      featured_image: this.featured_image,
      status: this.status,
      is_pinned: this.is_pinned
    };
  }

  /**
   * Convert to format for update (only changed fields)
   */
  toUpdateFormat(changes) {
    const format = {};
    if (changes.title !== undefined) format.title = changes.title;
    if (changes.slug !== undefined) format.slug = changes.slug;
    if (changes.excerpt !== undefined) format.excerpt = changes.excerpt;
    if (changes.content !== undefined) format.content = changes.content;
    if (changes.featured_image !== undefined) format.featured_image = changes.featured_image;
    if (changes.status !== undefined) format.status = changes.status;
    if (changes.is_pinned !== undefined) format.is_pinned = changes.is_pinned;
    return format;
  }
}

/**
 * BlogPostListItem for list views (without full content)
 */
export class BlogPostListItem {
  constructor(data) {
    this.post_id = data.post_id;
    this.title = data.title || '';
    this.slug = data.slug || '';
    this.excerpt = data.excerpt || null;
    this.featured_image = data.featured_image || null;
    this.status = data.status || POST_STATUS.DRAFT;
    this.is_pinned = data.is_pinned || false;
    this.author_name = data.author_name || null;
    this.published_at = data.published_at;
    this.updated_at = data.updated_at;
  }

  get isPublished() {
    return this.status === POST_STATUS.PUBLISHED;
  }

  get isPinned() {
    return this.is_pinned === true;
  }

  get formattedPublishDate() {
    if (!this.published_at) return null;
    return new Date(this.published_at).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  static fromApiResponse(data) {
    return new BlogPostListItem(data);
  }

  static fromApiResponseList(dataList = []) {
    return dataList.map(BlogPostListItem.fromApiResponse);
  }
}

